import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { EnumRoutePaths } from 'src/configs/routes';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useQuery } from 'src/hooks/useQuery';
import MainLayout from 'src/Layouts/MainLayout';
import WithSliderLayout from 'src/Layouts/WithSliderLayout';
import http from 'src/services/http';
import { setLng } from 'src/store/app/actions';
import { setOrder } from 'src/store/order/actions';
import { getUser } from 'src/store/user/actions';
import { IRootState } from 'src/types/store-types';
import { API_VERSION } from 'src/utils/constants';

import { Loader } from '../atoms/Loader/Loader';
import ProtectedRoute from '../common/ProtectedRoute';
import HomePage from '../pages/HomePage';

const Rates = lazy(() => import('../pages/Rates'));
const TourBookingPage = lazy(() => import('../pages/TourBookingPage'));
const ToursPage = lazy(() => import('../pages/ToursPage'));
const FloatingWhatsApp = lazy(() =>
  import('react-floating-whatsapp').then((module) => ({ default: module.FloatingWhatsApp }))
);
const Privacy = lazy(() => import('../pages/Privacy'));
const About = lazy(() => import('../pages/About'));
// const CategoriesPage = lazy(() => import('../pages/CategoriesPage'));
const CartPage = lazy(() => import('../pages/CartPage'));
const OrderChecking = lazy(() => import('../pages/OrderChecking'));
const _404_Page = lazy(() => import('../pages/404'));
const Blog = lazy(() => import('../pages/Blog'));
const SingleBlog = lazy(() => import('../pages/SingleBlog'));
const CityTours = lazy(() => import('../pages/CityTours'));
const FiltersPage = lazy(() => import('../pages/FiltersPage'));
const ProfileBonuses = lazy(() => import('../pages/ProfileBonuses'));
const Referrer = lazy(() => import('../pages/Referrer'));

const DEFAULT_LANGUAGE = 'am';
const LEGACY_LANGUAGE_QUERY_PARAM = 'lng';
const SUPPORTED_LANGUAGES = ['en', 'ru', 'am'];

const getSupportedLanguage = (lng?: string): string | undefined => {
  return SUPPORTED_LANGUAGES.find((language) => language.toLowerCase() === lng?.toLowerCase());
};

const getPathLanguage = (pathname: string): string | undefined => {
  return getSupportedLanguage(pathname.split('/')[1]);
};

const getDeviceType = (): string => {
  const userAgent = window.navigator.userAgent.toLowerCase();

  if (/mobile|iphone|ipod|android.*mobile/.test(userAgent)) {
    return 'mobile';
  }

  if (/ipad|tablet|android/.test(userAgent)) {
    return 'tablet';
  }

  return 'PC';
};

const withLanguagePath = (path: string): string => {
  return path === EnumRoutePaths.BASE ? '/:lng' : `/:lng${path}`;
};

const AnonimusRoutes: FC = () => {
  const queries = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const { lng } = useSelector((state: IRootState) => state.app);
  const setOrderDataAction = useConnectedAction(setOrder);

  const { i18n } = useTranslation();
  const setLngAction = useConnectedAction(setLng);

  const _getUser = useConnectedAction(getUser);
  const [canLoadFloatingWidgets, setCanLoadFloatingWidgets] = useState(false);

  useEffect(() => {
    const urlLanguage = getPathLanguage(location.pathname);
    const searchParams = new URLSearchParams(location.search);
    const legacyQueryLanguage = getSupportedLanguage(queries[LEGACY_LANGUAGE_QUERY_PARAM]);
    searchParams.delete(LEGACY_LANGUAGE_QUERY_PARAM);
    const nextSearch = searchParams.toString();

    if (!urlLanguage) {
      const activeLanguage = legacyQueryLanguage || getSupportedLanguage(lng) || DEFAULT_LANGUAGE;
      const pathWithoutTrailingSlash = location.pathname === '/' ? '' : location.pathname;

      navigate(
        {
          pathname: `/${activeLanguage}${pathWithoutTrailingSlash}`,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: location.hash,
        },
        { replace: true }
      );
      return;
    }

    if (queries[LEGACY_LANGUAGE_QUERY_PARAM]) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: location.hash,
        },
        { replace: true }
      );
    }

    setLngAction(urlLanguage);
    i18n.changeLanguage(urlLanguage);
  }, [i18n, lng, location.hash, location.pathname, location.search, navigate, queries, setLngAction]);

  const postRerralLinkVisitors = async (referralLink?: string): Promise<void> => {
    const device = getDeviceType();

    http.post(
      { path: `${API_VERSION}/users/visitors`, baseURL: process.env.REACT_APP_REMOTE as string },
      referralLink ? { referralLink, device } : { device }
    );
  };

  useEffect(() => {
    if (queries.referralLink) {
      sessionStorage.setItem('referralLink', queries.referralLink);
    }
    const refLink = queries.referralLink || sessionStorage.getItem('referralLink') || '';
    if (refLink) {
      setOrderDataAction({ name: 'referralLink', value: refLink });
    }
    if (queries.token) {
      localStorage.setItem('token', queries.token);
      _getUser();
    }
    const visitorTimer = window.setTimeout(() => {
      postRerralLinkVisitors(refLink);
    }, 6000);

    return () => window.clearTimeout(visitorTimer);
  }, [queries.referralLink, queries.token, setOrderDataAction, _getUser]);

  useEffect(() => {
    const idleTimer = window.setTimeout(() => {
      setCanLoadFloatingWidgets(true);
    }, 8000);

    return () => window.clearTimeout(idleTimer);
  }, []);

  return (
    <MainLayout>
      <section className="app-content-container">
        <Routes>
          <Route
            path={withLanguagePath(EnumRoutePaths.REFERRER)}
            element={
              <Suspense fallback={<Loader />}>
                <Referrer />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.SEARCH)}
            element={
              <Suspense fallback={<Loader />}>
                <FiltersPage />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.PRIVACY)}
            element={
              <Suspense fallback={<Loader />}>
                <Privacy />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.ABOUT)}
            element={
              <Suspense fallback={<Loader />}>
                <About />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.CITY_TOURS)}
            element={
              <Suspense fallback={<Loader />}>
                <CityTours />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.VIP_TOURS)}
            element={
              <WithSliderLayout>
                <Suspense fallback={<Loader />}>
                  <ToursPage headerName="vipTours" fromAiSuggestions={false} />
                </Suspense>
              </WithSliderLayout>
            }
          />

          <Route
            path={withLanguagePath(EnumRoutePaths.TOUR_BOOKING)}
            element={
              <WithSliderLayout>
                <Suspense fallback={<Loader />}>
                  <TourBookingPage />
                </Suspense>
              </WithSliderLayout>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.AI_SUGGESTIONS)}
            element={
              <WithSliderLayout>
                <Suspense fallback={<Loader />}>
                  <ToursPage headerName="aiSuggestions" fromAiSuggestions={true} />
                </Suspense>
              </WithSliderLayout>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.CART)}
            element={
              <Suspense fallback={<Loader />}>
                <CartPage />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.CHECKING)}
            element={
              <Suspense fallback={<Loader />}>
                <OrderChecking />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.RATES)}
            element={
              <Suspense fallback={<Loader />}>
                <Rates />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.SINGLE_BLOG)}
            element={
              <WithSliderLayout isBlog>
                <Suspense fallback={<Loader />}>
                  <SingleBlog />
                </Suspense>
              </WithSliderLayout>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.BLOG)}
            element={
              <Suspense fallback={<Loader />}>
                <Blog />
              </Suspense>
            }
          />
          <Route
            path={withLanguagePath(EnumRoutePaths.PROFILE_BONUSES)}
            element={
              <ProtectedRoute>
                <Suspense fallback={<Loader />}>
                  <ProfileBonuses />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path={withLanguagePath(EnumRoutePaths.BASE)} element={<HomePage />} />
          <Route
            path="*"
            element={
              <Suspense fallback={<Loader />}>
                <_404_Page headerName="incorrectRoute" />
              </Suspense>
            }
          />
        </Routes>
      </section>
      {canLoadFloatingWidgets && (
        <Suspense fallback={null}>
          <FloatingWhatsApp phoneNumber="+37493245235" accountName="TouRest.online" avatar="/images/pwa.png" />
        </Suspense>
      )}
    </MainLayout>
  );
};

export default AnonimusRoutes;
